import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject, Subscription, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { RoutingService } from 'src/app/services/routing.service';
import { ApiService } from 'src/app/services/api.service';
import { ScreenDimensionsService } from 'src/app/services/screen-dimensions.service';
import { LanguageService } from 'src/app/services/language.service';
import { AuthService } from 'src/app/services/auth.service';
import { EntityType } from 'src/app/enums/entity-type.enum';
import { ModelPageDto } from 'src/app/models/model-page-dto';
import { ControlType } from '../arcgis-map/constants/control-type.enum';
import { MapControlItem } from '../arcgis-map/constants/map-control-item.interface';
import { ARCGIS_MAP_LIFECYCLE } from '../arcgis-map/constants/map-life-cycle.interface';
import { MainMapService } from '../arcgis-map/services/main-map.service';
import { ArcGisMapComponent } from '../arcgis-map/arcgis-map.component';
import { Entity } from 'src/app/models/mvp-query-response-dto';

@Component({
  selector: 'app-model',
  templateUrl: './model.component.html',
  styleUrls: ['./model.component.scss'],
  providers: [
    {
      provide: ARCGIS_MAP_LIFECYCLE,
      useClass: MainMapService,
    },
  ],
})
export class ModelComponent implements OnInit, OnDestroy {
  private entityDetailsSubscription?: Subscription;
  private languageSubscription?: Subscription;
  closeClicked: Subject<void> = new Subject<void>();
  switchToMapTab: Subject<void> = new Subject<void>();

  modelId: number = -1;
  modelDetails: ModelPageDto | null = null;
  safeModelUrl: SafeResourceUrl | null = null;
  isLoading: boolean = true;
  error: string | null = null;
  currentLanguage$!: Observable<string>;
  currentLang: 'he' | 'en' = 'he';
  shapes: any[] = [];
  mapControls: MapControlItem[] = [];
  selectedEntityId: number | null = null;
  EntityType = EntityType;
  // The map lives in the entity page's map tab, so it can be created before or after the
  // model details arrive; whichever comes second draws the orthophoto.
  private mapRef: ArcGisMapComponent | null = null;

  constructor(
    private routingService: RoutingService,
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private screenDimensionsService: ScreenDimensionsService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.currentLanguage$ = this.languageService.currentLanguage$;

    this.languageSubscription = this.currentLanguage$.subscribe(lang => {
      this.currentLang = lang as 'he' | 'en';
      this.populateMapControls();
    });

    this.entityDetailsSubscription = this.routingService.entityDetails$.subscribe(entityDetails => {
      this.ngZone.run(() => {
        const { id, type } = entityDetails;
        if (!!id && type === EntityType.PHOTOGRAMMETRIC) {
          this.modelId = id;
          this.loadModelDetails();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.entityDetailsSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
    this.closeClicked.complete();
    this.switchToMapTab.complete();
  }

  private loadModelDetails(): void {
    if (this.modelId < 0) return;

    this.isLoading = true;
    this.error = null;
    this.safeModelUrl = null;

    this.authService.refreshMediaServiceCookies().subscribe(() => {
      this.apiService.getModelPage(this.modelId).subscribe({
        next: (response) => {
          this.modelDetails = response;
          if (response.externalUrl) {
            this.safeModelUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.externalUrl);
          }
          this.buildShapes();
          this.showOrthophoto();
          this.isLoading = false;
        },
        error: (err) => {
          this.error = err.status === 404
            ? 'iaa.general.modelUnavailable'
            : err.status === 403
              ? 'iaa.general.noPermissions'
              : 'iaa.general.error';
          this.isLoading = false;
        }
      });
    });
  }

  buildShapes(): void {
    this.shapes = [];
    if (!this.modelDetails?.mapInfo?.length) return;

    const title = this.currentLang === 'he'
      ? (this.modelDetails.modelNameHebrew || this.modelDetails.modelNameEnglish || '')
      : (this.modelDetails.modelNameEnglish || this.modelDetails.modelNameHebrew || '');

    this.modelDetails.mapInfo.forEach((shape: any) => {
      this.shapes.push({
        wkt: shape.shapeWkt,
        layerName: this.translate.instant('iaa.app.search.PhotogrammetryModels'),
        shapeId: `model-${this.modelDetails!.entityId}`,
        hidden: false,
        shapeData: {
          entity: {
            entityId: this.modelDetails!.entityId,
            entityType: 'photogrammetric_model'
          }
        },
        popupTemplate: { title },
        styledParams: {
          fillColor: [100, 150, 255, 0.25],
          borderColor: [50, 100, 200, 0.9],
          borderWidth: 3,
        }
      });
    });
  }

  onMapReady(map: ArcGisMapComponent): void {
    this.mapRef = map;
    this.showOrthophoto();
  }

  // Draws the model's orthophoto (a COG) on the map, above the model polygon. The model's
  // imageUrl is "<media service>/orthophoto/<orthophoto id>" when the model has an orthophoto
  // with an image - the same URL shape the results map uses for orthophoto entities.
  private showOrthophoto(): void {
    if (!this.mapRef) return;

    const imageUrl = this.modelDetails?.imageUrl;
    const orthophotoId = imageUrl?.match(/\/orthophoto\/(\d+)(?:[/?#]|$)/)?.[1];
    const orthophotos = orthophotoId
      ? [{ entityType: EntityType.ORTHOPHOTO, originEntityId: orthophotoId, imageUrl } as Entity]
      : [];
    // An empty list clears the orthophoto of a previously shown model. No zoom to the
    // orthophoto: the map keeps the zoom on the model polygon (extentAll).
    this.mapRef.displayOrthophotoEntities(orthophotos, false);
  }

  populateMapControls(): void {
    const position = this.currentLang === 'he' ? 'top-left' : 'top-right';
    this.mapControls = [
      { position, type: ControlType.FullScreen },
      { position, type: ControlType.Zoom },
      { position, type: ControlType.Locate, attributes: { scale: 100 } },
      {
        position,
        type: ControlType.BasemapGallery,
        expand: {
          id: 'basemap-gallery-expand',
          position,
          expandIcon: 'basemap',
          expandTooltip: this.translate.instant('iaa.map.expand-basemap-gallery'),
          mode: 'floating',
        },
      },
    ];
  }

  downloadModel(): void {
    if (this.modelDetails?.downloadUrl) {
      // Ensure the cookie is set/renewed silently before opening the download link
      this.authService.refreshMediaServiceCookies().subscribe(() => {
        window.open(this.modelDetails!.downloadUrl, '_blank', 'noopener,noreferrer');
      });
    }
  }
}

